/** Vietnamese labels for approval statuses */
export const STATUS_LABELS: Record<string, string> = {
    '': 'Chờ duyệt',
    approved: 'Đã duyệt',
    pending: 'Chờ duyệt',
    rejected: 'Từ chối',
}

/** Vietnamese labels for day-off types */
export const DAY_OFF_TYPE_LABELS: Record<string, string> = {
    normal: 'Nghỉ phép thường',
    annual: 'Nghỉ phép năm',
    unpaid: 'Nghỉ không lương',
    sick: 'Nghỉ ốm',
    maternity: 'Nghỉ thai sản',
    wedding: 'Nghỉ cưới',
    funeral: 'Nghỉ tang',
}

/** Vietnamese labels for business types */
export const BUSINESS_TYPE_LABELS: Record<string, string> = {
    business_trip: 'Công tác',
    meeting: 'Họp',
    training: 'Đào tạo',
}

export const getStatusLabel = (status: string): string =>
    STATUS_LABELS[status] || status

export const getDayOffTypeLabel = (type: string): string =>
    DAY_OFF_TYPE_LABELS[type] || type

export const getBusinessTypeLabel = (type: string): string =>
    BUSINESS_TYPE_LABELS[type] || type

export const WORK_DAY_TYPES = {
    NORMAL: 'Ngày bình thường',
    ANNUAL_LEAVE: 'Nghỉ phép năm',
    UNPAID_LEAVE: 'Nghỉ không phép',
    REMOTE: 'Ngày làm việc remote',
    HOLIDAY: 'Ngày nghỉ lễ, tết',
    BUSINESS_TRIP: 'Ngày công tác',
} as const

export const getWorkDayTypeColor = (
    type: string,
    workDayIn?: string,
    workDayOption?: number | null
) => {
    switch (type) {
        case WORK_DAY_TYPES.NORMAL:
            if (workDayIn && (workDayOption === 0 || workDayOption === null)) {
                const [hours, minutes] = workDayIn.split(':').map(Number)
                const isLate = hours > 8 || (hours === 8 && minutes > 30)
                if (isLate) {
                    return 'bg-[#FCEDED] text-error rounded-[16px]'
                }
            }
            return 'bg-[#EEFBF2] text-success rounded-[16px]'
        case WORK_DAY_TYPES.ANNUAL_LEAVE:
            return 'bg-[#EEFBF2] text-success rounded-[16px]'
        case WORK_DAY_TYPES.UNPAID_LEAVE:
            return 'bg-[#EEFBF2] text-success rounded-[16px]'
        case WORK_DAY_TYPES.REMOTE:
            return 'bg-blue-100 text-blue-800 rounded-[16px]'
        case WORK_DAY_TYPES.HOLIDAY:
            return 'bg-blue-100 text-blue-800 rounded-[16px]'
        case WORK_DAY_TYPES.BUSINESS_TRIP:
            return 'bg-blue-100 text-blue-800 rounded-[16px]'
        default:
            return 'bg-gray-100 text-gray-800 rounded-[16px]'
    }
}

export const formatWorkDayTime = (time?: number | null): string => {
    if (time == null || time === 0) return ''
    if (Number.isInteger(time)) return String(time)
    if (Number.isFinite(time)) return (time as number).toFixed(2)
    return ''
}

export const getWorkDayTooltipTitle = (
    workDayType: string,
    workDayOption?: number
) => {
    const leaveTypes = [
        WORK_DAY_TYPES.ANNUAL_LEAVE,
        WORK_DAY_TYPES.UNPAID_LEAVE,
        WORK_DAY_TYPES.BUSINESS_TRIP,
    ]

    if (leaveTypes.includes(workDayType as any)) {
        const days = workDayOption === 2 ? '1' : '0.5'
        return `${workDayType} (${days} ngày)`
    }

    return workDayType
}

export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// Standard work start times
const MORNING_START_HOUR = 8
const MORNING_START_MINUTE = 30
const AFTERNOON_START_HOUR = 13
const AFTERNOON_START_MINUTE = 0

/**
 * Calculate late minutes for a workday
 * @param workDayIn - Check-in time (format: "HH:mm")
 * @param workDayOption - 0: morning only, 1: afternoon only (nghỉ sáng/công tác sáng), 2: full day, null: treated as full day
 * @param dayOfWeek - Day of week (0 = Sunday, 6 = Saturday)
 * @returns Late minutes (0 if not late or should be skipped)
 */
export const calculateLateMinutes = (
    workDayIn?: string | null,
    workDayOption?: number | null,
    dayOfWeek?: number
): number => {
    // Skip weekends (Saturday = 6, Sunday = 0)
    if (dayOfWeek === 0 || dayOfWeek === 6) return 0

    // Skip if no check-in time
    if (!workDayIn) return 0

    const [hours, minutes] = workDayIn.split(':').map(Number)
    if (isNaN(hours) || isNaN(minutes)) return 0

    // Determine start time based on work_day_option
    // work_day_option = 1 → Nghỉ sáng / Công tác sáng → compare with 13:00
    // work_day_option = 0, 2, null → compare with 8:30
    const isAfternoonOnly = workDayOption === 1

    const startHour = isAfternoonOnly
        ? AFTERNOON_START_HOUR
        : MORNING_START_HOUR
    const startMinute = isAfternoonOnly
        ? AFTERNOON_START_MINUTE
        : MORNING_START_MINUTE

    const checkInMinutes = hours * 60 + minutes
    const startTimeMinutes = startHour * 60 + startMinute

    if (checkInMinutes > startTimeMinutes) {
        return checkInMinutes - startTimeMinutes
    }

    return 0
}

/**
 * Format late minutes for display
 * @param minutes - Total late minutes
 * @returns Formatted string (e.g., "1h 30m" or "45m")
 */
export const formatLateMinutes = (minutes: number): string => {
    if (minutes <= 0) return ''
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    if (hours > 0 && mins > 0) return `${hours}h ${mins}m`
    if (hours > 0) return `${hours}h`
    return `${mins}m`
}
